import { Navigate } from "react-router-dom";
import type { RouteObject } from "react-router-dom";
import { routeHandle } from "@/modules";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import EmailVerificationPage from "@/pages/EmailVerificationPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import PasswordResetSentPage from "@/pages/PasswordResetSentPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import OAuthCallbackPage from "@/pages/OAuthCallbackPage";
import SlackLinkPage from "@/pages/SlackLinkPage";
import SettingsPage from "@/pages/SettingsPage";
import { SelfieOnboardingPage } from "@/features/onboarding";
import FilePickerDemoPage from "@/pages/FilePickerDemoPage";
import PricingPage from "@/pages/PricingPage";
import SupportPage from "@/pages/SupportPage";
import SupportTicketsPage from "@/pages/SupportTicketsPage";
import SupportTicketDetailPage from "@/pages/SupportTicketDetailPage";
import CheckoutPage from "@/pages/CheckoutPage";
import OrdersPage from "@/pages/OrdersPage";
import OrderSuccessPage from "@/pages/OrderSuccessPage";
import OrderDetailPage from "@/pages/OrderDetailPage";
import { CartPage } from "@/pages/CartPage";
import { StreamingChat } from "@/features/chat/components";
import {
  PERSONA_SETUP_PATH,
  ONBOARDING_PATH,
  PersonaSetupPage,
} from "@/features/persona-setup";
import { OnboardingRouteGuard, PersonaSetupRouteGuard } from "./route-guards";
import { LAUNCHPAD_PATH } from "@/features/home/hooks/use-home";

/**
 * Modül dışı (shared) route'lar — sepet, ödeme, siparişler, ayarlar, destek,
 * chat, auth. Path'ler App.tsx'teki eski tanımlarla birebir aynı; yalnızca
 * guard/layout bilgisi `handle` üzerinden taşınıyor.
 *
 * Varsayılan: `auth: "private"`, `layout: "shell"`.
 */

const shell = (auth: "private" | "optional" | "public" = "private") =>
  routeHandle({ auth, layout: "shell" });
const bare = (auth: "private" | "optional" | "public" | "guest" = "private") =>
  routeHandle({ auth, layout: "bare" });

/**
 * Ana domain'de `/` — anasayfa: sohbet yok, Harmony'yi anlatır ve modüle
 * yönlendirir (lazy chunk). AI Models yalnızca kendi
 * kökünde (`/models`). Subdomain'de host modülünün kökü `/` olduğu için bu
 * route mount edilmez.
 */
const homeLazy = async () => ({
  Component: (await import("@/features/home")).HomePage,
});

export const rootRoute: RouteObject = {
  path: "/",
  lazy: homeLazy,
  handle: shell("optional"),
  // Eski "stacked layer" child'ları — davranış korunuyor
  children: [
    { path: "profile/*", element: null },
    { path: "settings/*", element: null },
  ],
};

export const sharedRoutes: RouteObject[] = [
  // ── Chat ────────────────────────────────────────────────────────────────
  // `draft-<nonce>` konuşma id'si değildir: ilk tur burada akar, id cevap bitince gelir.
  { path: "/chat/:id", element: <StreamingChat />, handle: shell() },
  // "Yeni sohbet" → Launchpad: modül seç, sohbet modülün dock'unda başlar
  {
    path: LAUNCHPAD_PATH,
    lazy: async () => ({
      Component: (await import("@/features/launchpad")).LaunchpadPage,
    }),
    handle: shell("optional"),
  },
  // Eski modülsüz composer sayfası kaldırıldı; her sohbet bir modüle ait → Launchpad
  { path: "/new-chat", element: <Navigate to={LAUNCHPAD_PATH} replace /> },

  // ── Commerce (modülden bağımsız tek sepet) ──────────────────────────────
  { path: "/cart", element: <CartPage />, handle: shell() },
  { path: "/checkout", element: <CheckoutPage />, handle: shell() },
  { path: "/orders", element: <OrdersPage />, handle: shell() },
  {
    path: "/orders/success/:orderGroupId",
    element: <OrderSuccessPage />,
    handle: shell(),
  },
  {
    path: "/orders/:orderGroupId",
    element: <OrderDetailPage />,
    handle: shell(),
  },

  // ── Abonelik dönüşleri ─────────────────────────────────────────────────
  // PaymentNavigationHandler bu adresleri yakalayıp `/?payment=…`e çevirir;
  // o ana kadar anasayfa çizilir
  { path: "/subscription-success", lazy: homeLazy, handle: shell() },
  { path: "/subscription-cancel", lazy: homeLazy, handle: shell() },

  // ── Ayarlar / fiyat / destek ───────────────────────────────────────────
  {
    path: "/settings",
    element: <Navigate to="/settings/profile" replace />,
    handle: bare("public"),
  },
  { path: "/settings/:tab", element: <SettingsPage />, handle: shell() },
  { path: "/pricing", element: <PricingPage />, handle: shell() },
  { path: "/support", element: <SupportPage />, handle: shell("optional") },
  {
    path: "/support/tickets",
    element: <SupportTicketsPage />,
    handle: shell("optional"),
  },
  {
    path: "/support/tickets/:id",
    element: <SupportTicketDetailPage />,
    handle: shell("optional"),
  },
  { path: "/file-picker-demo", element: <FilePickerDemoPage />, handle: shell() },

  // ── Post-login gate sayfaları (tam ekran) ──────────────────────────────
  {
    path: PERSONA_SETUP_PATH,
    element: (
      <PersonaSetupRouteGuard>
        <PersonaSetupPage />
      </PersonaSetupRouteGuard>
    ),
    handle: bare(),
  },
  {
    path: ONBOARDING_PATH,
    element: (
      <OnboardingRouteGuard>
        <SelfieOnboardingPage />
      </OnboardingRouteGuard>
    ),
    handle: bare(),
  },

  // ── Auth (yalnızca oturumsuz) ──────────────────────────────────────────
  { path: "/login", element: <LoginPage />, handle: bare("guest") },
  { path: "/register", element: <RegisterPage />, handle: bare("guest") },
  {
    path: "/verify-email",
    element: <EmailVerificationPage />,
    handle: bare("guest"),
  },
  {
    path: "/forgot-password",
    element: <ForgotPasswordPage />,
    handle: bare("guest"),
  },
  {
    path: "/password-reset-sent",
    element: <PasswordResetSentPage />,
    handle: bare("guest"),
  },
  {
    path: "/reset-password/:id/:token",
    element: <ResetPasswordPage />,
    handle: bare("guest"),
  },

  // ── OAuth / entegrasyon dönüşleri (guard yok) ──────────────────────────
  {
    path: "/auth/google/callback",
    element: <OAuthCallbackPage />,
    handle: bare("public"),
  },
  {
    path: "/auth/omuz/callback",
    element: <OAuthCallbackPage />,
    handle: bare("public"),
  },
  {
    path: "/auth/apple/callback",
    element: <OAuthCallbackPage />,
    handle: bare("public"),
  },
  { path: "/slack/link", element: <SlackLinkPage />, handle: bare("public") },

  // ── Catch-all: bilinmeyen adres → anasayfa ─────────────────────────────
  { path: "*", element: <Navigate to="/" replace /> },
];
